import type { PdfTemplate } from "../types";

/**
 * Rådgivning flere mål, flere porteføljer - Standard PDF-mal (Norsk)
 *
 * QP-150-variant av {@link advisoryMultiStandard}. Målseksjonen er trimmet til innhold på
 * målnivå (målinfo + risiko); dens tilknyttede `per_portfolio`-undernode holder porteføljeanalysen
 * (oversikt, prognose, X-Ray) og gjengis én gang per portefølje per mål. Formuesplan og kostnader
 * er på instansnivå — en avsluttende global seksjon som gjengis én gang til slutt.
 */
export const advisoryMultiPortfolioStandard: PdfTemplate = {
  key: "advisory-multi-portfolio-standard-no",
  category: "advisory",
  language: "nb_NO",
  name: "Rådgivning flere mål, flere porteføljer - Standard",
  description:
    "Rådgivningsdokumentasjon for flermåls, flerporteføljes rådgivning. Målinfo + risiko per mål; porteføljeanalyse (X-Ray) og prognose gjentas per portefølje; formuesplan og kostnader vises én gang til slutt.",
  badges: ["MiFID II", "Flere mål", "Flere porteføljer", "X-Ray"],

  tableStyleType: "alternating",
  tableHeaderBgColor: "#b70404",
  tableHeaderTextColor: "#ffffff",
  tableAlternatingColor: "#eecdcd",

  sections: [
    // === SECTION 1: Cover Page (global) ===
    {
      name: "Forside",
      scope: "global",
      order: 0,
      headerShowLine: false,
      footerShowNumbers: false,
      footerShowLine: false,
      pages: [
        {
          name: "Forside",
          order: 0,
          components: [
            { type: "spacer", order: 0, config: { height: 12 } },
            {
              type: "text",
              order: 1,
              config: {
                content: "Investeringsrådgivning for\n{{Client.Name}}",
                align: "center",
                fontSize: 30,
                maxWidth: 12,
              },
            },
          ],
        },
      ],
    },

    // === SECTION 2: Global Section (global) ===
    {
      name: "Kundeseksjon",
      scope: "global",
      order: 1,
      headerText: "Investeringsrapport for {{Client.Name}}",
      headerShowLine: true,
      footerShowNumbers: true,
      footerShowLine: true,
      pages: [
        {
          name: "Kundeprofil",
          order: 0,
          components: [
            { type: "heading", order: 0, config: { title: "Investeringsforslag" } },
            {
              type: "text",
              order: 1,
              columnPosition: "left",
              config: {
                content:
                  "Takk for tilliten. Din rådgiver er stolt over å presentere denne investeringsmuligheten for deg.",
              },
            },
            {
              type: "advisor_image",
              order: 2,
              columnPosition: "right",
              config: { width: 20, align: "left", shape: "circle" },
            },
            {
              type: "table",
              order: 3,
              config: {
                title: "Kundedata",
                description:
                  "Nedenfor er en oppsummering av din personlige informasjon og kontaktopplysninger fra rådgivningssamtalen.",
                rows: [
                  { col1: "Navn", col2: "{{Client.Name}}" },
                  { col1: "Type", col2: "{{Client.Type}}" },
                  { col1: "Kundesegment", col2: "{{Client.Team}}" },
                ],
              },
            },
            {
              type: "client_classification",
              order: 4,
              config: {
                title: "Kundeklassifisering",
                description:
                  "Klassifisering i henhold til MiFID II-regelverket basert på kundens kunnskap og erfaring.",
              },
            },
            {
              type: "product_assessment",
              order: 5,
              config: {
                title: "Produktvurdering",
                description:
                  "Oversikt over kundens tidligere erfaring med ulike investeringsprodukter.",
              },
            },
          ],
        },
      ],
    },

    // === SECTION 3: Goal Section (per_goal — kun målnivå) ===
    {
      name: "Målseksjon",
      scope: "per_goal",
      order: 2,
      headerText: "Investeringsrapport for {{Client.Name}}",
      headerShowLine: true,
      footerShowNumbers: true,
      footerShowLine: true,
      pages: [
        {
          name: "Mål",
          order: 0,
          components: [
            { type: "heading", order: 0, config: { title: "Mål" } },
            {
              type: "goal_info",
              order: 1,
              columnPosition: "left",
              config: {
                title: "Målinformasjon",
                description: "Detaljer om investeringsmålet.",
              },
            },
            {
              type: "risk_assessment",
              order: 2,
              columnPosition: "right",
              config: {
                title: "Risikovurdering",
                description: "Risikoprofilvurdering basert på kundens svar.",
              },
            },
          ],
        },
      ],
    },

    // === SECTION 4: Portfolio Section (per_portfolio — gjentas per portefølje) ===
    {
      name: "Porteføljeseksjon",
      scope: "per_portfolio",
      order: 3,
      headerText: "Investeringsrapport for {{Client.Name}}",
      headerShowLine: true,
      footerShowNumbers: true,
      footerShowLine: true,
      pages: [
        {
          name: "Porteføljeanalyse",
          order: 0,
          components: [
            { type: "heading", order: 0, config: { title: "Portefølje" } },
            {
              type: "portfolio_display",
              order: 1,
              columnPosition: "left",
              config: {
                title: "Porteføljeoversikt",
                description: "Oversikt over porteføljebeholdningen.",
                showWeights: true,
                showIsin: true,
                showAmount: true,
              },
            },
            {
              type: "portfolio_forecast",
              order: 2,
              columnPosition: "right",
              config: {
                title: "Porteføljeprognose",
                description: "Forventet porteføljeutvikling basert på Monte Carlo-simuleringer.",
                showChart: true,
                showTable: true,
              },
            },
            { type: "page_break", order: 3, config: {} },
            {
              type: "portfolio_xray",
              order: 4,
              columnPosition: "left",
              config: {
                title: "Aksjestilboks",
                description: "Morningstar aksjestilklassifisering.",
                displayMode: "equity_style",
                showChart: true,
                showTable: false,
              },
            },
            {
              type: "portfolio_xray",
              order: 5,
              columnPosition: "right",
              config: {
                title: "Landeksponering",
                displayMode: "country_exposure",
                showChart: true,
                showTable: true,
                enableFollowupComparison: true,
              },
            },
            {
              type: "portfolio_xray",
              order: 6,
              columnPosition: "left",
              config: {
                title: "Aktivaklasser",
                description: "Aktivaallokering med Morningstar X-Ray",
                displayMode: "asset_allocation",
                showChart: true,
                showTable: true,
                enableFollowupComparison: true,
              },
            },
            {
              type: "portfolio_xray",
              order: 7,
              columnPosition: "right",
              config: {
                title: "Regioneksponering",
                displayMode: "regional_exposure",
                showChart: true,
                showTable: true,
                enableFollowupComparison: true,
              },
            },
            {
              type: "asset_allocation",
              order: 8,
              columnPosition: "left",
              config: {
                title: "Aktivaallokering",
                description: "Aktivaallokering med interne aktivaklasser",
                showChart: true,
                showTable: true,
                enableFollowupComparison: true,
              },
            },
            {
              type: "portfolio_xray",
              order: 9,
              columnPosition: "right",
              config: {
                title: "Aksjesektor",
                displayMode: "stock_sector",
                showChart: true,
                showTable: true,
                enableFollowupComparison: true,
              },
            },
            {
              type: "portfolio_xray",
              order: 10,
              columnPosition: "right",
              config: {
                title: "Obligasjonssektor",
                displayMode: "bond_sector",
                showChart: true,
                showTable: true,
                enableFollowupComparison: true,
              },
            },
            {
              type: "portfolio_xray",
              order: 11,
              config: {
                title: "Topp 10 beholdninger",
                displayMode: "top_holdings",
                showChart: true,
                showTable: false,
              },
            },
          ],
        },
      ],
    },

    // === SECTION 5: Formuesplan og kostnader (global — etter mål, på instansnivå) ===
    // Speiler flytens `after_goals`-sider: gjengis én gang til slutt, etter hvert mål + dets
    // porteføljer. Byggeren gjengir globale seksjoner i rekkefølge, så en plassering etter
    // per_portfolio-seksjonen havner helt til slutt.
    {
      name: "Formuesplan og kostnader",
      scope: "global",
      order: 4,
      headerText: "Investeringsrapport for {{Client.Name}}",
      headerShowLine: true,
      footerShowNumbers: true,
      footerShowLine: true,
      pages: [
        {
          name: "Formuesplan",
          order: 0,
          components: [
            { type: "heading", order: 0, config: { title: "Formuesplan" } },
            {
              type: "wealth_plan",
              order: 1,
              columnPosition: "left",
              config: {
                title: "Formuesplan",
                description:
                  "Oversikt over planlagte uttak og forventet utvikling av din samlede formue.",
              },
            },
            {
              type: "expected_value",
              order: 2,
              columnPosition: "right",
              config: {
                title: "Forventet verdi",
                showChart: true,
                showSummaryTable: true,
                description: "Oversikt over forventet verdiutvikling av porteføljen over tid.",
              },
            },
          ],
        },
        {
          name: "Kostnader",
          order: 1,
          components: [
            { type: "heading", order: 0, config: { title: "Kostnader" } },
            {
              type: "cost_chart",
              order: 1,
              columnPosition: "left",
              config: {
                title: "Kostnadsprognose (rullerende)",
                displayMode: "rolling",
                description: "Kostnadsprognose over rullerende perioder.",
              },
            },
            {
              type: "cost_table",
              order: 2,
              columnPosition: "right",
              config: {
                title: "Kostnadstabell",
                description: "Oversikt over alle kostnader knyttet til investeringen din.",
              },
            },
            {
              type: "portfolio_costs",
              order: 3,
              columnPosition: "left",
              config: {
                title: "Rådgivningskostnader",
                description: "Plattform- og rådgivningskostnader for investeringsporteføljen din.",
              },
            },
            {
              type: "product_costs",
              order: 4,
              columnPosition: "right",
              config: {
                title: "Produktkostnader",
                description: "Løpende produktkostnader.",
              },
            },
          ],
        },
      ],
    },
  ],
};
